import { useMemo, useCallback, useState } from "react";

import { Flex } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useShallow } from "zustand/react/shallow";

import { IntlMessageId } from "shared/lang";
import { BottomSheet } from "ui/BottomSheet/BottomSheet";
import { Button } from "ui/Button/Button";
import {
  ArrowLineDownIcon,
  ArrowLineUpIcon,
  BagIcon,
  CalendarIcon,
  CaretDownIcon,
  HashCircleIcon,
  LayersIcon,
  TagIcon,
} from "ui/Icons/Icons";
import { TitleM, BodyS, BodyM } from "ui/Typography/Typography";

import { useTGTGBagStore } from "../stores/useTGTGBagStore";

function CategoriesCollapsible({
  allowedCategories,
  formattedCategories,
}: {
  allowedCategories: string[];
  formattedCategories: string;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Flex direction="column" gap="s50" cursor="pointer" onClick={() => setIsOpen((prev) => !prev)}>
      <Flex align="center" gap="s200">
        <Flex color="pinkFlink.500" boxSize="1.25rem" align="center" justify="center">
          <TagIcon boxSize="1.5rem" />
        </Flex>
        <BodyS color="grey.600" flex="1">
          <FormattedMessage id="pages.tgtg-bag-selection-page.bag-guidelines.eligible-categories" />
          {` (${allowedCategories.length})`}
        </BodyS>
        <CaretDownIcon
          boxSize="1.25rem"
          color="grey.600"
          transform={isOpen ? "rotate(180deg)" : undefined}
          transition="transform 0.2s"
        />
      </Flex>
      <Flex pl="2.25rem">
        <BodyM noOfLines={isOpen ? undefined : 1}>{formattedCategories}</BodyM>
      </Flex>
    </Flex>
  );
}

function GuidelineItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactElement;
  label: IntlMessageId;
  value: string;
}) {
  return (
    <Flex direction="column" gap="s50">
      <Flex align="center" gap="s200">
        <Flex color="pinkFlink.500" boxSize="1.25rem" align="center" justify="center">
          {icon}
        </Flex>
        <BodyS color="grey.600">
          <FormattedMessage id={label} />
        </BodyS>
      </Flex>
      <Flex pl="2.25rem">
        <BodyM>{value}</BodyM>
      </Flex>
    </Flex>
  );
}

export function GuidelinesBottomSheet({
  isBottomSheetOpen,
  closeBottomSheet,
}: {
  isBottomSheetOpen: boolean;
  closeBottomSheet: () => void;
}) {
  const intl = useIntl();

  const {
    bagKey,
    minimumValue,
    maximumThreshold,
    maximumUnitsPerSku,
    minimumDistinctSkus,
    allowedCategories,
    minimumExpirationDate,
  } = useTGTGBagStore(
    useShallow((state) => ({
      bagKey: state.bagKey,
      minimumValue: state.minimumValue,
      maximumThreshold: state.maximumThreshold,
      maximumUnitsPerSku: state.maximumUnitsPerSku,
      minimumDistinctSkus: state.minimumDistinctSkus,
      allowedCategories: state.allowedCategories,
      minimumExpirationDate: state.minimumExpirationDate,
    })),
  );

  const parseCategories = useCallback(
    (categories: string[]): string => {
      return categories
        .map((category) =>
          intl.formatMessage({
            id: `pages.tgtg-bag-selection-page.bag-guidelines.categories.${category}` as IntlMessageId,
          }),
        )
        .join(", ");
    },
    [intl],
  );

  const formattedCategories = useMemo(() => {
    if (allowedCategories.length > 0) {
      return parseCategories(allowedCategories);
    }
    return intl.formatMessage({
      id: "pages.tgtg-bag-selection-page.bag-guidelines.all-categories" as IntlMessageId,
    });
  }, [allowedCategories, parseCategories, intl]);

  const formattedMinimumValue = useMemo(
    () =>
      intl.formatNumber(minimumValue ?? 0, {
        style: "currency",
        currency: "EUR",
      }),
    [intl, minimumValue],
  );

  const formattedMaximumValue = useMemo(
    () =>
      intl.formatNumber((minimumValue ?? 0) + maximumThreshold, {
        style: "currency",
        currency: "EUR",
      }),
    [intl, maximumThreshold, minimumValue],
  );

  const formattedExpirationDate = useMemo(
    () =>
      minimumExpirationDate
        ? intl.formatDate(minimumExpirationDate, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        : "-",
    [intl, minimumExpirationDate],
  );

  return (
    <BottomSheet isOpen={isBottomSheetOpen} onClose={closeBottomSheet}>
      <Flex direction="column" px="s200" pb="s200" gap="s100">
        <Flex align="center" gap="s200" py="s200">
          <BagIcon boxSize="1.5rem" color="pinkFlink.500" />
          <TitleM>
            <FormattedMessage
              id="pages.tgtg-bag-selection-page.bag-guidelines.title"
              values={{
                bagName: intl.formatMessage({
                  id: `pages.tgtg-bag-selection-page.bag-types.${bagKey}` as IntlMessageId,
                }),
              }}
            />
          </TitleM>
        </Flex>

        {allowedCategories.length > 0 ? (
          <CategoriesCollapsible
            allowedCategories={allowedCategories}
            formattedCategories={formattedCategories}
          />
        ) : (
          <GuidelineItem
            icon={<TagIcon boxSize="1.5rem" />}
            label="pages.tgtg-bag-selection-page.bag-guidelines.eligible-categories"
            value={formattedCategories}
          />
        )}

        <GuidelineItem
          icon={<ArrowLineDownIcon boxSize="1.5rem" />}
          label="pages.tgtg-bag-selection-page.bag-guidelines.minimum-bag-value"
          value={formattedMinimumValue}
        />

        <GuidelineItem
          icon={<ArrowLineUpIcon boxSize="1.5rem" />}
          label="pages.tgtg-bag-selection-page.bag-guidelines.maximum-bag-value"
          value={formattedMaximumValue}
        />

        <GuidelineItem
          icon={<HashCircleIcon boxSize="1.5rem" />}
          label="pages.tgtg-bag-selection-page.bag-guidelines.maximum-quantity-per-item"
          value={String(maximumUnitsPerSku ?? 0)}
        />

        <GuidelineItem
          icon={<LayersIcon boxSize="1.5rem" />}
          label="pages.tgtg-bag-selection-page.bag-guidelines.minimum-distinct-items"
          value={String(minimumDistinctSkus ?? 0)}
        />

        <GuidelineItem
          icon={<CalendarIcon boxSize="1.5rem" />}
          label="pages.tgtg-bag-selection-page.bag-guidelines.minimum-expiration-date"
          value={formattedExpirationDate}
        />

        <Button variant="primary" size="lg" mt="s200" isFullWidth onClick={closeBottomSheet}>
          <FormattedMessage id="pages.tgtg-bag-selection-page.bag-guidelines.got-it" />
        </Button>
      </Flex>
    </BottomSheet>
  );
}
